import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Mail, Phone, Calendar, ArrowLeft, ExternalLink } from 'lucide-react';

const Contact = () => {
  const [businessName, setBusinessName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const name = localStorage.getItem('eybBusinessName');
    if (name) {
      setBusinessName(name);
    }
  }, []);

  const contactOptions = [
    {
      id: 'appointment',
      title: 'Book an Appointment',
      description: 'Schedule a consultation to discuss your business needs',
      icon: Calendar,
      color: '#29ABE2',
      action: () => window.open('https://eybconsulting.com.au/get-started/', '_blank')
    },
    {
      id: 'email',
      title: 'Email EYB',
      description: 'Send us an email and we\'ll get back to you soon',
      icon: Mail,
      color: '#1E88C7',
      action: () => window.location.href = 'mailto:info@eybconsulting.com.au'
    },
    {
      id: 'call',
      title: 'Call EYB',
      description: 'Speak directly with our team',
      icon: Phone,
      color: '#3A7AA0',
      action: () => window.location.href = 'tel:0422731122'
    }
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-mesh">
      {/* Layered animated background */}
      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none"></div>
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(91,160,208,0.35), transparent 70%)' }}></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(0,51,102,0.22), transparent 70%)', animationDelay: '1.5s' }}></div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_e62fe2c4-7b06-4359-9dcf-9bd67772605b/artifacts/k7n3nl06_Screenshot%202024-07-25%20103325.png" 
            alt="EYB Consulting" 
            className="h-12 w-auto cursor-pointer smooth-transition hover:opacity-80"
            onClick={() => navigate('/')}
          />
          <Button
            onClick={() => navigate('/journey')}
            variant="ghost"
            className="flex items-center gap-2 smooth-transition"
            style={{ color: '#29ABE2' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Journey Map
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-6 py-16">
        {/* Title section */}
        <div className="text-center mb-16 animate-fadeInUp">
          <h1 className="text-5xl md:text-6xl font-semibold mb-6" style={{ color: '#0B0F14' }}>
            {businessName ? `Ready to see how EYB can help ${businessName}?` : 'Ready to get started?'}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Choose your preferred way to connect with our team. We're here to help you achieve your business goals.
          </p>
        </div>

        {/* Contact options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {contactOptions.map((option, index) => {
            const Icon = option.icon;
            return (
              <div
                key={option.id}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
              >
                <div
                  onClick={option.action}
                  className="group bg-white rounded-2xl p-8 cursor-pointer hover-lift stage-card-glow border-2 border-transparent hover:border-sky-300 smooth-transition h-full flex flex-col"
                >
                  {/* Glow effect */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 smooth-transition blur-xl"
                    style={{ background: `${option.color}20`, zIndex: -1 }}
                  ></div>

                  {/* Icon */}
                  <div className="mb-6">
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center group-hover:scale-110 smooth-transition"
                      style={{ background: `linear-gradient(135deg, ${option.color}15, ${option.color}30)` }}
                    >
                      <Icon className="w-10 h-10" style={{ color: option.color }} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-3" style={{ color: option.color }}>
                    {option.title}
                  </h3>
                  <p className="text-gray-600 mb-6 flex-grow">
                    {option.description}
                  </p>

                  {/* Action indicator */}
                  <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: option.color }}>
                    <span>Connect now</span>
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 smooth-transition" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info card */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.4s', opacity: 0, animationFillMode: 'forwards' }}>
          <div className="bg-gradient-to-br from-sky-50 to-blue-100 rounded-3xl p-10 border-2 border-sky-200">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: '#0B2545' }}>
                What happens next?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#29ABE2' }}>1</div>
                  <p className="text-gray-700 font-medium">We'll discuss your current challenges and goals</p>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#29ABE2' }}>2</div>
                  <p className="text-gray-700 font-medium">We'll create a tailored plan for your business</p>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2" style={{ color: '#29ABE2' }}>3</div>
                  <p className="text-gray-700 font-medium">We'll work together to achieve your vision</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="text-center mt-12 animate-fadeInUp" style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}>
          <p className="text-sm text-gray-500 max-w-2xl mx-auto">
            This interactive tool is for information only and does not constitute financial advice. 
            All advice provided by EYB Consulting will be tailored to your specific circumstances.
          </p>
        </div>

        {/* Restart journey button */}
        <div className="text-center mt-8 animate-fadeInUp" style={{ animationDelay: '0.6s', opacity: 0, animationFillMode: 'forwards' }}>
          <Button
            onClick={() => {
              localStorage.removeItem('eybBusinessName');
              navigate('/');
            }}
            variant="outline"
            className="smooth-transition tracking-wide font-bold hover:bg-[#0B2545] hover:text-white"
            style={{ borderColor: '#0B2545', color: '#0B2545', background: 'transparent', borderRadius: '9999px', padding: '10px 24px' }}
          >
            Start Over with a Different Business
          </Button>
        </div>
      </main>
    </div>
  );
};

export default Contact;
